'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { Slot } from '../slot';
import { DropdownFloatingContext } from './context/dropdown-floating.context';
import { DropdownRootContext } from './context/dropdown-root.context';
import {
  dropdownContentPositionerStyles,
  dropdownContentStyles,
} from './styles/dropdown-content.styles';
import type { DropdownContentPropsInterface } from './types/dropdown-content.types';

/**
 * @since 10.0.0
 */
export const DropdownContent = forwardRef<HTMLDivElement, DropdownContentPropsInterface>(
  (props: DropdownContentPropsInterface, forwardedRef) => {
    const { dropdown } = useContext(DropdownRootContext);
    const floating = useContext(DropdownFloatingContext);
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = props;

    const contentPositionerStyles = css(dropdownContentPositionerStyles);
    const contentStyles = css(dropdownContentStyles, cssProp);

    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      modal: false,
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([dropdown.refs.setFloating, forwardedRef]);

    // Don't use tabIndex from useInteractions mergeProps (default -1 on floating).
    // FloatingFocusManager sets tabindex on the floating focus element as needed (CDS-5822).
    const { tabIndex: _floatingTabIndex, ...floatingProps } = floating.getFloatingProps(rest);

    const Component = asChild ? Slot : 'div';

    const content = useDecoratedSlotContent({
      children,
      asChild,
      template: (childContent) => (
        <div
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Dropdown.Content"
          className={cx('c11n', contentStyles, className)}
          style={{ ...dropdown.floatingTransitionStyles, ...style }}
        >
          {childContent}
        </div>
      ),
      dependencies: [contentStyles, className, dropdown.floatingTransitionStyles, style],
    });

    return (
      <FloatingFocusManager context={floating.context} {...focusManagerProps}>
        <Component
          ref={mergedRefs}
          aria-labelledby={dropdown.labelId}
          aria-describedby={dropdown.descriptionId}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Dropdown.ContentPositioner"
          className={cx('c11n', contentPositionerStyles)}
          style={{
            ...dropdown.floatingStyles,
            visibility: dropdown.middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
          }}
          {...floatingProps}
        >
          {content}
        </Component>
      </FloatingFocusManager>
    );
  },
);

DropdownContent.displayName = 'Dropdown.Content';
